import { ROUTE_NAMES } from '../router/route-names';

/**
 * Bugsnag defaults `event.context` to `window.location.pathname`, and Bugsnag titles an issue
 * `<errorClass> in <context>`. Our pathnames embed the tenant slug, charge point id, EVSE
 * reference and payment ref, so a single recurring defect opens a new issue — and a new
 * auto-filed story — for every session it hits. Normalising the pathname to its route pattern
 * collapses that fan-out to one issue per screen. The raw URL is still reported as metadata.
 */

// Route literals src/router/index.tsx spells out inline rather than through ROUTE_NAMES. Every
// other literal segment is derived from ROUTE_NAMES below, so renaming a route there keeps this
// list correct; a segment missing from both is treated as a parameter and coarsens the context.
const INLINE_ROUTE_LITERALS = ['tenant', 'cp', 'evse'];

const PARAM_PLACEHOLDER = ':param';

// ROUTE_NAMES.root is the only non-constant entry (buildAppRootPath reads window.location), but it
// only ever yields ':param<n>' segments, which the filter drops — so this set is boot-URL invariant.
const ROUTE_LITERALS = new Set<string>(
  Object.values(ROUTE_NAMES)
    .flatMap(routeName => routeName.split('/'))
    .concat(INLINE_ROUTE_LITERALS)
    .filter(segment => segment.length > 0 && !segment.startsWith(':'))
);

export const normalizeBugsnagContext = (context: string): string => {
  // A context that is not a pathname was set deliberately and must be left alone.
  if (!context.startsWith('/')) {
    return context;
  }

  const segments = context.split('/').filter(segment => segment.length > 0);

  // Drop the deployment's app-root prefix (see build-app-root-path.ts) so the same screen yields
  // the same context whether it is served from /tenant/... or /lt/tenant/...
  const firstRouteSegment = segments.findIndex(segment => ROUTE_LITERALS.has(segment));
  const routeSegments = firstRouteSegment === -1 ? segments : segments.slice(firstRouteSegment);

  return `/${routeSegments.map(segment => (ROUTE_LITERALS.has(segment) ? segment : PARAM_PLACEHOLDER)).join('/')}`;
};
